import React, { useState } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import TableSortLabel from '@mui/material/TableSortLabel';
import { useSelectedInstancesStore, useSelectedRDSStore } from '../../../state/Zuststore';
import { Grid } from '@mui/material';

export default function MainTable(props) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const columns = props.columns || [];
  const rows = props.rows || [];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const { selectedRunningInstances, selectedStoppedInstances, addSelectedRunningInstance, addSelectedStoppedInstance } = useSelectedInstancesStore();
  const { addSelectedRunningDB, addSelectedStoppedDB, selectedRunningDBS, selectedStoppedDBS } = useSelectedRDSStore();

  const handleCheckboxChange = (selectedInstanceId, instanceState, dbIdentifier, Status) => {
    if (instanceState === 'running') {
      addSelectedRunningInstance(selectedInstanceId);
    } else if (instanceState === 'stopped') {
      addSelectedStoppedInstance(selectedInstanceId);
    } else if (Status === 'available') {
      addSelectedRunningDB(dbIdentifier);
    } else if (Status === 'stopped') {
      addSelectedStoppedDB(dbIdentifier);
    }
  };

  const determineCellColorec2 = (column, value, oldValue, newValue) => {
    switch (column.id) {
      case 'Instance state':
      case 'name':
      case 'Instance ID':
      case 'Instance type':
      case 'Availability zone':
      case 'Public IPv4 DNS':
      case 'Public IPv4 address':
      case 'IPv6 IPs':
      case 'Monitoring':
      case 'Security group name':
      case 'Key name':
      case 'Launch Time':
      case 'Platfrom details':
        return value === oldValue ? '#ff6600' : value === newValue ? '#009900' : '';
      default:
        return '';
    }
  };

  const determineCellColorRDS = (column, value, oldValue, newValue) => {
    switch (column.id) {
      case 'DB identifier':
      case 'Status':
      case 'Engine':
      case 'Region':
      case 'Size':
      case 'VCP':
      case 'MultiAZ':
        return value === oldValue ? '#ff6600' : value === newValue ? '#009900' : '';
      default:
        return '';
    }
  };

  const determineCellColorEBS = (column, value, oldValue, newValue) => {
    switch (column.id) {
      case 'Name':
      case 'Volumeid':
      case 'Type':
      case 'Size':
      case 'IOPS':
      case 'Availabilityzone':
      case 'Attachedresources':
      case 'Volumestate':
      case 'Encryption':
      case 'MultiAttachenabled':
        return value === oldValue ? '#ff6600' : value === newValue ? '#009900' : '';
      default:
        return '';
    }
  };

  const determineCellColorLambda = (column, value, oldValue, newValue) => {
    switch (column.id) {
      case 'Name':
      case 'Description':
      case 'Packagetype':
      case 'Runtime':
      case 'Lastmodified':
        return value === oldValue ? '#ff6600' : value === newValue ? '#009900' : '';
      default:
        return '';
    }
  };

  const isToday = (date) => {
    const today = new Date();
    return date?.getMonth() === today.getMonth() &&
      date?.getDate() === today.getDate() &&
      date?.getFullYear() === today.getFullYear();
  };

  const handleRequestSort = (columnId) => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrderBy(columnId);
    setOrder(isAsc ? 'desc' : 'asc');
  };

  const sortedRows = () => {
    const comparator = (a, b) => {
      const valueA = typeof a[orderBy] === 'string' ? a[orderBy].toLowerCase() : a[orderBy];
      const valueB = typeof b[orderBy] === 'string' ? b[orderBy].toLowerCase() : b[orderBy];

      // Check if values are numbers
      const isNumericA = !isNaN(valueA);
      const isNumericB = !isNaN(valueB);

      // If both values are numeric, compare as numbers
      if (isNumericA && isNumericB) {
        return order === 'asc' ? valueA - valueB : valueB - valueA;
      }

      // If one or both values are not numeric, compare as strings
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return order === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      }

      // If one of the values is numeric and the other is not,
      // numeric values should always come first
      return isNumericA ? -1 : 1;
    };

    return order === 'asc' ? rows.slice().sort(comparator) : rows.slice().sort(comparator).reverse();
  };

  return (
    <Grid container xs={12} md={12} lg={12} >
      <TableContainer style={{ maxHeight: 400, maxWidth: '65vw' }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns?.map((column) => (
                <TableCell
                  sx={{ fontWeight: '600', color: '#504e70', fontSize: "16px", backgroundColor: "#E6E6FA" }}
                  key={column.id}
                  align={column.align}
                  // style={{ minWidth: column.minWidth }}
                >
                  {column.id !== 'select' ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows()
              ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              ?.map((row) => (
                <TableRow hover role="checkbox" tabIndex={-1} key={row.code}>
                  {columns.map((column) => {
                    const value = row[column.id];
                    let textColor1 = null; // Initialize as null to avoid undefined errors

                    // Determine text color based on column ID and value
                    if (column.id === 'Volumestate') {
                      textColor1 = value === 'in-use' ? 'green' : '#1E90FF';
                    }

                    const textColor = determineCellColorec2(column, value, row.oldValue, row.newValue);
                    const textColorEBS = determineCellColorEBS(column, value, row.oldValue, row.newValue);
                    const textColorRDS = determineCellColorRDS(column, value, row.oldValue, row.newValue);
                    const textColorLambda = determineCellColorLambda(column, value, row.oldValue, row.newValue);

                    const isSelected = row['Instance state'] === 'running' || row['Status'] === 'available'
                      ? selectedRunningInstances.includes(row['Instance ID']) || selectedRunningDBS.includes(row['DB identifier'])
                      : selectedStoppedInstances.includes(row['Instance ID']) || selectedStoppedDBS.includes(row["DB identifier"]);

                    const capitalizedValue = column.id === 'Instance state' ? value?.toUpperCase() : value;

                    let Ebscost = column.id === 'OnDemandMonthlyStoragePriceValue' ? `${value.toFixed(2)} $` : capitalizedValue;
                    let EbsSize = column.id === 'Size' ? `${value} GB` : capitalizedValue;

                    return (
                      <TableCell key={column.id} align={column.align} style={{ color: textColor || textColorRDS || textColorEBS || textColorLambda || textColor1 }}>
                        {column.id === 'select' ? (
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleCheckboxChange(row['Instance ID'], row['Instance state'], row['DB identifier'], row['Status'])}
                          />
                        ) : (
                          column.id === 'Date' && isToday(new Date(value))
                            ? 'Today'
                            : column.format && typeof value === 'number'
                              ? column.format(value)
                              : (capitalizedValue &&
                                Ebscost &&
                                EbsSize)
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Grid>
  );
}
